import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useRanking } from '@/context/RankingContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight, ExternalLink } from 'lucide-react';

const NEWS_ITEMS = [
  {
    title: "How U.S. News Calculated the Rankings",
    source: "U.S. News",
    date: "2024",
    link: "https://www.usnews.com/education/best-colleges/articles/how-us-news-calculated-the-rankings"
  },
  {
    title: "Ranking Criteria and Weights Explained",
    source: "U.S. News",
    date: "2024",
    link: "https://www.usnews.com/education/best-colleges/articles/ranking-criteria-and-weights"
  },
  {
    title: "Factors That Influence Student College Choice",
    source: "NCES",
    date: "2024",
    link: "https://nces.ed.gov/pubs2019/2019119/index.asp"
  },
  {
    title: "Social Mobility Rankings",
    source: "U.S. News",
    date: "2024",
    link: "https://www.usnews.com/best-colleges/rankings/national-universities/social-mobility"
  }
];

const QUICK_ACTIONS = [
  {
    title: "Dashboard",
    description: "View comprehensive analytics",
    path: "/dashboard",
    icon: ExternalLink
  },
  {
    title: "Rankings",
    description: "Compare universities",
    path: "/rankings",
    icon: ExternalLink
  },
  {
    title: "Metrics",
    description: "Explore ranking factors",
    path: "/metrics",
    icon: ExternalLink
  }
];

export function HomePage() {
  const navigate = useNavigate();
  const { depaulRank } = useRanking();

  return (
    <div className="container mx-auto px-4 min-h-[calc(100vh-4rem)] flex items-center">
      <div className="grid grid-cols-12 gap-8 w-full">
        {/* Main Content */}
        <div className="col-span-12 lg:col-span-8">
          <Card className="border-[#4FAADD]/20 h-full">
            <CardContent className="p-8">
              {/* Header Section */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
                <div>
                  <h1 className="text-4xl font-bold text-[#274791]">
                    DePaul University
                  </h1>
                  <p className="text-xl text-[#4478B9] mt-2">
                    National Universities Ranking
                  </p>
                </div>

                {/* Enhanced Rank Circle */}
                <div className="relative w-44 h-44 lg:w-52 lg:h-52">
                  <div className="absolute inset-0">
                    <svg className="w-full h-full transform -rotate-90">
                      {/* Background circle with gradient */}
                      <defs>
                        <linearGradient id="circleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#E5E7EB" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#E5E7EB" stopOpacity="0.1" />
                        </linearGradient>
                      </defs>
                      {/* Background circle */}
                      <circle
                        cx="50%"
                        cy="50%"
                        r="42%"
                        className="stroke-[#E5E7EB]"
                        strokeWidth="6"
                        fill="url(#circleGradient)"
                      />
                      {/* Progress circle */}
                      <circle
                        cx="50%"
                        cy="50%"
                        r="42%"
                        className="stroke-[#274791]"
                        strokeWidth="6"
                        fill="transparent"
                        strokeDasharray={2 * Math.PI * 0.42 * 52}
                        strokeDashoffset={2 * Math.PI * 0.42 * 52 * (1 - depaulRank/400)}
                        strokeLinecap="round"
                        style={{
                          filter: "drop-shadow(0 0 6px rgba(39, 71, 145, 0.3))"
                        }}
                      />
                    </svg>
                    {/* Center content with enhanced styling */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-6xl font-bold text-[#274791] tracking-tight">
                        #{depaulRank}
                      </span>
                      <span className="text-sm font-medium text-[#4478B9] mt-1">
                        Current Rank
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Grid with enhanced shadows */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                {/* All KPIs in one row */}
                <div className="bg-white rounded-lg p-4 shadow-[0_2px_8px_rgba(79,170,221,0.07)] border border-[#4FAADD]/20">
                  <p className="text-sm font-medium text-[#4FAADD]">Overall Score</p>
                  <h3 className="text-2xl font-bold text-[#274791] mt-1">55.0</h3>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-[0_2px_8px_rgba(79,170,221,0.07)] border border-[#4FAADD]/20">
                  <p className="text-sm font-medium text-[#4FAADD]">Outcomes Rank</p>
                  <h3 className="text-2xl font-bold text-[#274791] mt-1">#148</h3>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-[0_2px_8px_rgba(79,170,221,0.07)] border border-[#4FAADD]/20">
                  <p className="text-sm font-medium text-[#4FAADD]">Social Mobility</p>
                  <h3 className="text-2xl font-bold text-[#274791] mt-1">#118</h3>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-[0_2px_8px_rgba(79,170,221,0.07)] border border-[#4FAADD]/20">
                  <p className="text-sm font-medium text-[#4FAADD]">Faculty Resources</p>
                  <h3 className="text-2xl font-bold text-[#274791] mt-1">#254</h3>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-[0_2px_8px_rgba(79,170,221,0.07)] border border-[#4FAADD]/20">
                  <p className="text-sm font-medium text-[#4FAADD]">Financial Resources</p>
                  <h3 className="text-2xl font-bold text-[#274791] mt-1">#263</h3>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-[0_2px_8px_rgba(79,170,221,0.07)] border border-[#4FAADD]/20">
                  <p className="text-sm font-medium text-[#4FAADD]">Research Impact</p>
                  <h3 className="text-2xl font-bold text-[#274791] mt-1">#226</h3>
                </div>
              </div>

              {/* Quick Actions with enhanced hover effects */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                {QUICK_ACTIONS.map((action) => (
                  <motion.div
                    key={action.path}
                    whileHover={{ 
                      scale: 1.02,
                      transition: { type: "spring", stiffness: 400, damping: 10 }
                    }}
                    className="cursor-pointer"
                    onClick={() => navigate(action.path)}
                  >
                    <div className="bg-white rounded-lg p-4 shadow-[0_2px_8px_rgba(79,170,221,0.07)] border border-[#4FAADD]/20 hover:border-[#274791]/20 hover:shadow-[0_4px_12px_rgba(39,71,145,0.1)] transition-all duration-300">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-[#274791]">{action.title}</h3>
                          <p className="text-sm text-[#4FAADD] mt-1">{action.description}</p>
                        </div>
                        <action.icon className="w-5 h-5 text-[#4FAADD]" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* News Section */}
        <div className="col-span-12 lg:col-span-4">
          <Card className="border-[#4FAADD]/20 h-full">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-[#274791] mb-6">Latest News</h2>
              <div className="space-y-4">
                {NEWS_ITEMS.map((item, index) => (
                  <motion.a
                    key={index}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                    whileHover={{ 
                      x: 4,
                      transition: { type: "spring", stiffness: 400, damping: 10 }
                    }}
                  >
                    <div className="bg-white rounded-xl p-4 shadow-[0_2px_8px_rgba(79,170,221,0.07)] border border-[#4FAADD]/10 hover:border-[#274791]/20 hover:shadow-[0_4px_12px_rgba(39,71,145,0.1)] transition-all duration-300">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-medium text-[#274791] line-clamp-2">{item.title}</h3>
                          <div className="flex items-center text-sm text-[#4478B9] mt-2">
                            <span>{item.source}</span>
                            <span className="mx-2">•</span>
                            <span>{item.date}</span>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#274791] mt-1 flex-shrink-0" />
                      </div>
                    </div>
                  </motion.a>
                ))}
              </div>

              <Button
                onClick={() => window.open('https://www.usnews.com/best-colleges', '_blank')}
                className="w-full mt-6 bg-[#274791] hover:bg-[#274791]/90 text-white"
              >
                View All News
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}